import {
  extractMarkdownLinkHrefs,
  resolveMarkdownFileLinkMeta,
  type MarkdownFileLinkMeta,
} from "~/markdown-links";

export const REPLY_FILES_PREVIEW_LIMIT = 4;

/** Merge prose references with turn files through the inline-link path rules.
 * Ignore fenced examples and external links, and deduplicate by workspace path. */

export function replyFiles(
  text: string,
  cwd?: string,
  changedFiles: ReadonlyArray<{ path: string; kind: string }> = [],
): MarkdownFileLinkMeta[] {
  if (!cwd) return [];
  const prose = withoutFencedExamples(text);
  const references = [...extractMarkdownLinkHrefs(prose)];
  for (const match of prose.matchAll(/`([^`\n]+)`/g)) references.push(match[1]!);
  const unlinked = prose
    .replace(/\[[^\]]*]\(\s*(?:<[^>\n]+>|[^\s)]+)(?:\s+["'][^"']*["'])?\s*\)/g, "")
    .replace(/`[^`\n]+`/g, "");
  for (const match of unlinked.matchAll(
    /(?:^|[\s(])([A-Za-z0-9_./-]+\.(?:pdf|xlsx|xlsm|xls|docx|doc|pptx|ppt|html?|csv|tsv|md|txt|zip|png|jpe?g|svg|webp|gif|mp4|mp3|json))(?=$|[\s),.;!?])/gi,
  ))
    references.push(match[1]!);
  // Turn metadata also covers generated files omitted from the reply.
  const deleted = new Set(
    changedFiles
      .filter((file) => /^(?:deleted?|removed|d)$/i.test(file.kind))
      .map(
        (file) =>
          normalizeFile(file.path.startsWith("/") ? file.path : `./${file.path}`, cwd)
            ?.workspaceRelativePath,
      )
      .filter((path): path is string => Boolean(path)),
  );
  for (const file of changedFiles) {
    const reference = file.path.startsWith("/") ? file.path : `./${file.path}`;
    const path = normalizeFile(reference, cwd)?.workspaceRelativePath;
    if (path && !deleted.has(path)) references.push(reference);
  }
  const files = new Map<string, MarkdownFileLinkMeta>();
  for (const reference of references) {
    const file = normalizeFile(reference, cwd);
    if (file?.workspaceRelativePath && !deleted.has(file.workspaceRelativePath))
      files.set(file.workspaceRelativePath, file);
  }
  return [...files.values()];
}

/** A streaming reply may end before its closing fence arrives. */
function withoutFencedExamples(text: string): string {
  const prose: string[] = [];
  let fence: { marker: string; length: number } | null = null;
  for (const line of text.split("\n")) {
    const match = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
    if (fence) {
      if (
        match &&
        match[1]![0] === fence.marker &&
        match[1]!.length >= fence.length &&
        match[2]!.trim() === ""
      )
        fence = null;
    } else if (match && (match[1]![0] !== "`" || !match[2]!.includes("`"))) {
      fence = { marker: match[1]![0]!, length: match[1]!.length };
    } else {
      prose.push(line);
    }
  }
  return prose.join("\n");
}

/** Use the same canonical workspace path for prose and change metadata. */
function normalizeFile(reference: string, cwd: string): MarkdownFileLinkMeta | null {
  const file = resolveMarkdownFileLinkMeta(reference, cwd);
  if (!file?.workspaceRelativePath) return null;
  const parts: string[] = [];
  for (const part of file.workspaceRelativePath.split("/")) {
    if (part === "..") {
      if (!parts.length) return null;
      parts.pop();
    } else if (part && part !== ".") parts.push(part);
  }
  const relativePath = parts.join("/");
  return relativePath ? { ...file, workspaceRelativePath: relativePath } : null;
}
